import {
  WorkflowError,
  platformFieldNameToNodeColumnMap,
  tableStore,
  NodeColumns,
  TableNode,
} from '..';
import { BotCompilerError } from '../../utils';

const DUPLICATE_NODE_NUMBER_MESSAGE =
  'Duplicate node number. Node numbers must be unique.';

const DUPLICATE_NODE_NUMBER_ERROR_MESSAGES_FROM_API = {
  NODE_NUMBER: 'node number',
  DUPLICATE_NODE_NUMBER:
    'Bot has duplicate node numbers. This is not allowed. Please fix.',
};

export const mergeDuplicateNodeNumberErrors = (
  rows: TableNode[],
  existingErrors: WorkflowError[]
): WorkflowError[] => {
  const byNodeNumber = new Map<
    number,
    { row: TableNode; rowIndex: number }[]
  >();

  rows.forEach((row, rowIndex) => {
    const raw = row[NodeColumns.nodeNumber];
    if (raw === null || raw === undefined) return;
    const num = Number(raw);
    if (!Number.isInteger(num)) return;
    const list = byNodeNumber.get(num) ?? [];
    list.push({ row, rowIndex });
    byNodeNumber.set(num, list);
  });

  const duplicateErrors: WorkflowError[] = [];
  const colId = NodeColumns.nodeNumber;
  const colIndex = tableStore.getColumnIndex(colId);
  const colIndexOrNull = colIndex === -1 ? null : colIndex;

  byNodeNumber.forEach((entries) => {
    if (entries.length <= 1) return;
    entries.forEach(({ row, rowIndex }) => {
      const nodeNumber = row[NodeColumns.nodeNumber] ?? null;
      duplicateErrors.push({
        colId,
        colIndex: colIndexOrNull,
        rowIndex,
        errorMessage: DUPLICATE_NODE_NUMBER_MESSAGE,
        cellNumber: null,
        nodeNumber: nodeNumber === undefined ? null : nodeNumber,
        rowNumber: rowIndex + 1,
      });
    });
  });

  const withoutDuplicateNodeErrors = existingErrors.filter(
    (e) => e.errorMessage !== DUPLICATE_NODE_NUMBER_MESSAGE
  );
  return [...withoutDuplicateNodeErrors, ...duplicateErrors];
};

export const buildWorkflowErrors = (
  errors: BotCompilerError[],
  rows: TableNode[]
): WorkflowError[] => {
  const generatedErrors = errors
    .flatMap(
      ({
        err_msgs,
        cell_num,
        node_num,
        node_index,
        cell_index,
        row_num,
        column,
      }) => {
        const nodeNumber = node_num ?? null;
        const rowNumber = row_num ? row_num - 1 : null;
        const rIndex = row_num ? row_num - 2 : null;

        return err_msgs?.map(({ field_name, error_description }) => {
          const columnKey = platformFieldNameToNodeColumnMap[field_name || ''];
          const colIndex = cell_index || tableStore.getColumnIndex(columnKey);
          const rowIndex =
            node_index ||
            rIndex ||
            tableStore.getSortedRowIndex(
              tableStore
                .getAllSortedRows()
                .find((row) => row.nodeNumber === node_num)?.id || ''
            );

          return {
            colId: (column as NodeColumns) ?? columnKey,
            colIndex: colIndex === -1 ? null : colIndex,
            rowIndex: rowIndex === -1 ? null : rowIndex,
            errorMessage: error_description,
            cellNumber: cell_num === -1 ? null : cell_num,
            nodeNumber: node_num === -1 ? null : nodeNumber,
            rowNumber: row_num === -1 ? null : rowNumber,
          };
        });
      }
    )
    .filter((item) => item !== null);

  const hasNodeNumberError = generatedErrors.some(
    ({ errorMessage }) =>
      errorMessage ===
        DUPLICATE_NODE_NUMBER_ERROR_MESSAGES_FROM_API.NODE_NUMBER ||
      errorMessage ===
        DUPLICATE_NODE_NUMBER_ERROR_MESSAGES_FROM_API.DUPLICATE_NODE_NUMBER
  );

  let result = generatedErrors;

  if (hasNodeNumberError) {
    result = generatedErrors
      .filter(
        ({ errorMessage }) =>
          errorMessage !==
          DUPLICATE_NODE_NUMBER_ERROR_MESSAGES_FROM_API.NODE_NUMBER
      )
      .map((error) =>
        error.errorMessage ===
        DUPLICATE_NODE_NUMBER_ERROR_MESSAGES_FROM_API.DUPLICATE_NODE_NUMBER
          ? {
              ...error,
              errorMessage: DUPLICATE_NODE_NUMBER_MESSAGE,
            }
          : error
      );
  }

  return mergeDuplicateNodeNumberErrors(rows, result);
};
